
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Ban, Zap, RotateCcw } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { Bike } from '@/services/types';
import { SpeedLimitDialog } from '@/components/bikes/SpeedLimitDialog';
import { toggleBikeMovementBlock } from '@/pages/admin/bikes/services/movementControlApi';
import { bulkResetRestrictions } from '@/services/bulkMovementControlApi';

interface BikeActionControlsProps {
  bike: Bike;
  onBack: () => void;
  onActionComplete: () => void;
}

export const BikeActionControls: React.FC<BikeActionControlsProps> = ({
  bike,
  onBack,
  onActionComplete
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showSpeedLimitDialog, setShowSpeedLimitDialog] = useState(false);

  const handleToggleBlock = async () => {
    setIsLoading(true);
    try {
      const newBlocked = !bike.movement_blocked;
      const result = await toggleBikeMovementBlock(bike.id, newBlocked);
      
      if (result.success) {
        toast.success(
          newBlocked 
            ? 'Велосипед успешно заблокирован' 
            : 'Велосипед успешно разблокирован'
        );
        onActionComplete();
      } else {
        toast.error('Ошибка при изменении состояния блокировки');
      }
    } catch (error) {
      toast.error('Ошибка при выполнении операции');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetRestrictions = async () => {
    setIsLoading(true);
    try {
      const result = await bulkResetRestrictions([bike.id]);
      
      if (result.success && result.updatedCount > 0) {
        toast.success('Ограничения сброшены');
        onActionComplete();
      } else {
        toast.error('Нет активных ограничений для сброса');
      }
    } catch (error) {
      toast.error('Ошибка при сбросе ограничений');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSpeedLimitSuccess = () => {
    setShowSpeedLimitDialog(false);
    toast.success('Ограничение скорости установлено');
    onActionComplete();
  };

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h3 className="font-medium">Управление велосипедом</h3>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {bike.model} ({bike.serialNumber})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">GPS-тег:</span>
                <div>{bike.gps_tag || 'Не указан'}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Статус:</span>
                <div>
                  <Badge variant={bike.status === 'available' ? 'default' : 'secondary'}>
                    {bike.status === 'available' ? 'Доступен' : 
                     bike.status === 'in_use' ? 'В аренде' : 'Обслуживание'}
                  </Badge>
                </div>
              </div>
              {bike.courierName && (
                <div>
                  <span className="text-muted-foreground">Курьер:</span>
                  <div>{bike.courierName}</div>
                </div>
              )}
              {bike.rental_human_number && (
                <div>
                  <span className="text-muted-foreground">Номер сделки:</span>
                  <div>{bike.rental_human_number}</div>
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              {bike.movement_blocked && (
                <Badge variant="destructive">Заблокирован</Badge>
              )}
              {bike.speed_limit && (
                <Badge variant="outline">{bike.speed_limit} км/ч</Badge>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                onClick={handleToggleBlock}
                disabled={isLoading}
                variant={bike.movement_blocked ? "outline" : "destructive"}
                size="sm"
              >
                <Ban className="h-4 w-4 mr-1" />
                {bike.movement_blocked ? 'Разблокировать' : 'Заблокировать'}
              </Button>

              <Button
                onClick={() => setShowSpeedLimitDialog(true)}
                disabled={isLoading}
                variant="outline"
                size="sm"
              >
                <Zap className="h-4 w-4 mr-1" />
                Ограничить скорость
              </Button>

              {(bike.movement_blocked || bike.speed_limit) && (
                <Button
                  onClick={handleResetRestrictions}
                  disabled={isLoading}
                  variant="outline"
                  size="sm"
                >
                  <RotateCcw className="h-4 w-4 mr-1" />
                  Сбросить ограничения
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <SpeedLimitDialog
        selectedBikes={[bike]}
        open={showSpeedLimitDialog}
        onOpenChange={setShowSpeedLimitDialog}
        onConfirm={(speedLimit) => {
          console.log('Setting speed limit:', speedLimit);
          handleSpeedLimitSuccess();
        }}
      />
    </>
  );
};
